import type { CustomScrollbarProps } from '@lved/ui/components/shared/ui/CustomScrollbar';
import type { ResizeHandleProps } from '@lved/ui/components/shared/ui/ResizeHandle';
import type { useAssetsStore as UseAssetsStore } from '@lved/ui/store/assetsStore';
import { ReactWidget } from '@theia/core/lib/browser';
import { inject, injectable, postConstruct } from '@theia/core/shared/inversify';
import { FileStat } from '@theia/filesystem/lib/common/files';
import { WorkspaceService } from '@theia/workspace/lib/browser';
import { ComponentType } from 'react';

type AssetsPanelModule = {
    AssetsPanel: ComponentType;
    ImageAssetDetails: ComponentType;
    useAssetsStore: typeof UseAssetsStore;
    CustomScrollbar: ComponentType<CustomScrollbarProps>;
    ResizeHandle: ComponentType<ResizeHandleProps>;
};

/**
 * Left-sidebar panel that surfaces the project image-asset manager from
 * @lved/ui.  Available in both code mode and design mode.
 */
@injectable()
export class AssetsPanel extends ReactWidget {
    static readonly ID: string = 'widget:lvgl-assets-panel';
    static readonly LABEL: string = 'Assets';

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    private module: AssetsPanelModule | undefined;
    private loadError: string | undefined;
    private startQueue: Promise<void> = Promise.resolve();

    @postConstruct()
    protected init(): void {
        this.id = AssetsPanel.ID;
        this.title.label = AssetsPanel.LABEL;
        this.title.caption = AssetsPanel.LABEL;
        this.title.closable = false;
        this.title.iconClass = 'codicon codicon-file-media';
        this.doInit();
    }

    protected getProjectPath(): string | null {
        const root: FileStat | undefined = this.workspaceService.tryGetRoots()[0];
        return root?.resource.path.toString() ?? null;
    }

    protected async doInit(): Promise<void> {
        try {
            const [assetsMod, detailsMod, uiMod, scrollbarMod, resizeMod] = await Promise.all([
                import('@lved/ui/components/shared/ui/Design/AssetsPanel'),
                import('@lved/ui/components/shared/ui/Design/ImageAssetDetails'),
                import('@lved/ui') as unknown as Promise<AssetsPanelModule>,
                import('@lved/ui/components/shared/ui/CustomScrollbar'),
                import('@lved/ui/components/shared/ui/ResizeHandle')
            ]);
            this.module = {
                AssetsPanel: (assetsMod as unknown as { default: ComponentType }).default,
                ImageAssetDetails: (detailsMod as unknown as { default: ComponentType }).default,
                useAssetsStore: uiMod.useAssetsStore,
                CustomScrollbar: (scrollbarMod as unknown as { default: ComponentType<CustomScrollbarProps> }).default,
                ResizeHandle: (resizeMod as unknown as { default: ComponentType<ResizeHandleProps> }).default
            };

            const { useAssetsStore } = this.module;

            // Start assets sync for the current project.
            this.enqueueStart(this.getProjectPath());

            // Re-start if the workspace root changes.
            const unsubscribeWorkspace = this.workspaceService.onWorkspaceChanged(() => {
                this.enqueueStart(this.getProjectPath());
            });
            this.toDispose.push(unsubscribeWorkspace);
            this.toDispose.push({ dispose: () => useAssetsStore.getState().stop() });
        } catch (error: unknown) {
            this.loadError = error instanceof Error ? error.message : 'Failed to load Assets panel.';
        }
        this.update();
    }

    protected enqueueStart(projectPath: string | null): Promise<void> {
        if (!this.module) return Promise.resolve();
        this.startQueue = this.startQueue
            .then(async () => {
                if (this.isDisposed || !this.module) return;
                await this.module.useAssetsStore.getState().start(projectPath);
            })
            .catch(() => undefined);
        return this.startQueue;
    }

    protected render(): JSX.Element {
        if (this.loadError) {
            return <p style={{ padding: '8px', color: 'var(--theia-errorForeground)' }}>{this.loadError}</p>;
        }
        if (!this.module) {
            return <p style={{ padding: '8px' }}>Loading Assets…</p>;
        }

        const { AssetsPanel, ImageAssetDetails, CustomScrollbar, ResizeHandle } = this.module;

        return (
            <div
                className="lvgl-assets-panel-root"
                style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            >
                <CustomScrollbar className="lvgl-panel-assets-scroll">
                    <AssetsPanel />
                </CustomScrollbar>
                <ResizeHandle resizeTarget="previous" orientation="horizontal" />
                <CustomScrollbar className="lvgl-panel-asset-details-scroll">
                    <ImageAssetDetails />
                </CustomScrollbar>
            </div>
        );
    }
}
